import { Injectable } from '@angular/core';
import { Headers, RequestOptions, Response } from '@angular/http';
import { UploadInput } from 'ngx-uploader';
import { Observable } from 'rxjs';
import {
  DeviceLocation,
  DeviceSettings,
} from '../models/device-settings.model';
import { Device, DevicePaginationResponse } from '../models/device.model';
import { Globals } from './../helpers';
import { HttpInterceptor } from './http-error.interceptor';

export interface MassMetadata {
  users: string[];
  contents: string[];
  apps: string[];
  // address:string
  config: DeviceSettings;
}

@Injectable()
export class DeviceService {
  private deviceServiceUrl: string;

  constructor(private http: HttpInterceptor, private globals: Globals) {
    this.deviceServiceUrl = globals.url + 'devices/';
  }

  public getAll(page?, limit?, filters?): Observable<DevicePaginationResponse> {
    let url = this.globals.url + 'devices';
    if (typeof page == 'number' && typeof limit == 'number') {
      url = this.prepareUrlFilters(url, { ...filters, page, limit });
    } else {
      url = this.prepareUrlFilters(url, filters);
    }
    return this.http.get(url).map((response: Response) => response.json());
  }
  public getAllCount(filters?) {
    const url = this.prepareUrlFilters(this.globals.url + 'devices/count', filters);
    return this.http.get(url).map((response: Response) => response.json());
  }

  public getById(deviceId: string) {
    return this.http
      .get(this.deviceServiceUrl + deviceId)
      .map((response: Response) => response.json());
  }
  public overview() {
    return this.http
      .get(this.deviceServiceUrl + 'overview')
      .map((response: Response) => response.json());
  }
  public getC3versions() {
    return this.http.get(this.deviceServiceUrl + 'c3versions').map((response: Response) => response.json());
  }

  public checkLicense(licenseId) {
    return this.http.get(this.globals.url + 'license/' + licenseId, {
      headers: new Headers({ headerkey: ',tgBAHX>9!OLNrW%M:=3' }),
    }); // .map((response: Response) => response.json());
  }
  public isLicenseRegistered(licenseId) {
    return this.http.get(`${this.globals.url}license/${licenseId}/registered`, {
      headers: new Headers({ headerkey: ',tgBAHX>9!OLNrW%M:=3' }),
    }).map((response: Response) => response.json());
  }

  public create(device) {
    return this.http
      .post(this.deviceServiceUrl, device, {
        headers: new Headers({ headerkey: ',tgBAHX>9!OLNrW%M:=3' }),
      })
      .map((response: Response) => response.json());
  }
  public update(deviceId: string, device: Device) {
    return this.http
      .put(this.deviceServiceUrl + deviceId, device)
      .map((response: Response) => response.json());
  }
  public delete(deviceId: string) {
    return this.http
      .delete(this.deviceServiceUrl + deviceId)
      .map((response: Response) => response.json());
  }

  public getDeviceUsers(deviceId: string) {
    return this.http
      .get(this.deviceServiceUrl + deviceId + '/users')
      .map((response: Response) => response.json());
  }

  public massCreation(file: File, metadata: MassMetadata) {
    const formData = new FormData();
    formData.append('file', file);
    if (metadata.contents) { metadata.contents.forEach((content) => { formData.append('contents[]', content); }); }
    if (metadata.users) { metadata.users.forEach((content) => { formData.append('users[]', content); }); }
    if (metadata.apps) { metadata.apps.forEach((content) => { formData.append('applications[]', content); }); }
    if (metadata.config.distributionMode) { formData.append('distributionMode', metadata.config.distributionMode); }
    if (metadata.config.maxRecvKbps) { formData.append('maxRecvKbps', metadata.config.maxRecvKbps); }
    if (metadata.config.maxSendKbps) { formData.append('maxSendKbps', metadata.config.maxSendKbps); }
    if (metadata.config.pollingInterval) { formData.append('pollingInterval', metadata.config.pollingInterval); }
    if (metadata.config.scheduled) {
      formData.append('scheduledActive', '' + metadata.config.scheduled.active);
      formData.append('scheduledStart', '' + metadata.config.scheduled.start);
      formData.append('scheduledEnd', '' + metadata.config.scheduled.end);
    }
    return this.http
      .post(this.deviceServiceUrl + 'mass', formData)
      .map((response: Response) => response.json());
  }
  public massCheckCSV(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    return this.http
      .post(this.deviceServiceUrl + 'mass/csv-check', formData)
      .map((response: Response) => response.json());
  }

  public addDeviceToUser(userId: string, deviceId: string) {
    return this.http
      .put(this.deviceServiceUrl + deviceId + '/users/' + userId, null)
      .map((response: Response) => response.json());
  }
  public removeDeviceFromUser(userId: string, deviceId: string) {
    return this.http
      .delete(this.deviceServiceUrl + deviceId + '/users/' + userId)
      .map((response: Response) => response.json());
  }

  public getDeviceContents(deviceId: string) {
    return this.http
      .get(this.deviceServiceUrl + deviceId + '/contents')
      .map((response: Response) => response.json());
  }

  public subscribeContentToDevice(contentId: string, deviceId: string) {
    return this.http
      .put(this.deviceServiceUrl + deviceId + '/contents/' + contentId, null)
      .map((response: Response) => response.json());
  }
  public subscribeContentsToDevice(deviceId: string, contentIds: string[]) {
    return this.http
      .put(this.deviceServiceUrl + deviceId + '/contents/', {
        contents: contentIds,
      })
      .map((response: Response) => response.json());
  }
  public unsubscribeContentToDevice(contentId: string, deviceId: string) {
    return this.http
      .delete(this.deviceServiceUrl + deviceId + '/contents/' + contentId)
      .map((response: Response) => response.json());
  }
  public unsubscribeAllContents(deviceId: string) {
    return this.http
      .delete(this.deviceServiceUrl + deviceId + '/contents/')
      .map((response: Response) => response.json());
  }

  /* App */
  public getDeviceApps(deviceId: string) {
    return this.http
      .get(this.deviceServiceUrl + deviceId + '/applications')
      .map((response: Response) => response.json());
  }

  public subscribeAppToDevice(appId: string, deviceId: string) {
    return this.http
      .put(this.deviceServiceUrl + deviceId + '/applications/' + appId, null)
      .map((response: Response) => response.json());
  }
  public subscribeAppsToDevice(deviceId: string, appIds: string[]) {
    return this.http
      .put(this.deviceServiceUrl + deviceId + '/applications/', {
        applications: appIds,
      })
      .map((response: Response) => response.json());
  }
  public unsubscribeAppToDevice(appId: string, deviceId: string) {
    return this.http
      .delete(this.deviceServiceUrl + deviceId + '/applications/' + appId)
      .map((response: Response) => response.json());
  }
  public unsubscribeAllApplications(deviceId: string) {
    return this.http
      .delete(this.deviceServiceUrl + deviceId + '/applications/')
      .map((response: Response) => response.json());
  }

  public importC3Data() {
    const event: UploadInput = {
      method: 'PUT',
      url: this.deviceServiceUrl + 'import-data',
      type: 'uploadAll',
      headers: this.http.jwt(),
    };
    return event;
  }

  public emitEventToDevice(deviceId: string, event: string) {
    return this.http
      .post(this.deviceServiceUrl + deviceId + '/events', { event })
      .map((response: Response) => response.json());
  }

  // allows search with special chars. ex: #
  private prepareUrlFilters(url, filters) {
    const params: URLSearchParams = new URLSearchParams();
    if (!filters || !Object.keys(filters).length) { return url; }
    Object.entries(filters).forEach(([key, value]) => {
      params.append(key, value.toString());
    });
    const paramsStr = params.toString();
    if (paramsStr) { url += '?' + paramsStr; }
    return url;
  }
}
